import type { HotelDestinationPayload } from '@/src/api/requests/search-hotels'
import { useGetHotelAutoSuggestQuery } from '@/src/api/rtkQuery/hotels/hotelAutoSuggestApi'
import { HOTEL_AUTO_SUGGEST_DEBOUNCE_MS, HOTEL_AUTO_SUGGEST_MIN_CHARS } from '@/src/constants/hotels'
import { saveHotelLastSearch } from '@/src/hooks/hotels/useHotelLastSearch'
import { getFormattedDate } from '@/src/utils/date'
import dayjs from 'dayjs'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface HotelRoomConfig {
    adults: number
    children: number
    /** One age per child, RN parity. Seeded with DEFAULT_CHILD_AGE until a picker exists. */
    childAges: number[]
}

export type HotelDestinationKind = 'city' | 'hotel'

export type { HotelDestinationPayload }

export interface HotelDestinationSuggestion {
    key: string
    kind: HotelDestinationKind
    /** Top line — the property name, or the city's full location. */
    primary: string
    /** Second line — the property's location. Empty for a city. */
    secondary: string
    /** Whole row on one line, shown in the input once the row is picked. */
    label: string
    /** Properties in the city. Null for a property row, which shows no count. */
    propertyCount: number | null
    payload: HotelDestinationPayload
}

export const MIN_ROOMS = 1
export const MAX_ROOMS = 8
export const MIN_ADULTS_PER_ROOM = 1
export const MAX_ADULTS_PER_ROOM = 6
export const MIN_CHILDREN_PER_ROOM = 0
export const MAX_CHILDREN_PER_ROOM = 4

/** Matches RN's RoomGuestSelector default; upstream rejects children with no age. */
const DEFAULT_CHILD_AGE = 7

export const DEFAULT_ROOM_CONFIG: HotelRoomConfig = { adults: 1, children: 0, childAges: [] }

// Grow/shrink the age list to match the child count, keeping ages already chosen.
export const resizeChildAges = (childAges: number[], children: number): number[] => {
    const next = childAges.slice(0, children)
    while (next.length < children) next.push(DEFAULT_CHILD_AGE)
    return next
}

// searchHotels CSV-per-room encoding, RN parity: rooms split by "|", ages by ","
// (e.g. "5,8|0"). Empty string when no room has children.
export const encodeChildrenAges = (rooms: HotelRoomConfig[]): string => {
    if (!rooms.some(room => room.children > 0)) return ''
    return rooms.map(room => (room.children > 0 ? room.childAges.join(',') : '')).join('|')
}

const addDays = (date: Date, days: number) => dayjs(date).add(days, 'day').toDate()

const startOfDay = (date: Date) => dayjs(date).startOf('day')

const isSameOrBefore = (a: Date, b: Date) =>
    startOfDay(a).isBefore(startOfDay(b)) || startOfDay(a).isSame(startOfDay(b))

/** Seed values for the SRP modifier, which opens pre-filled from the current search. */
export interface HotelSearchInitialValues {
    destination?: string
    destinationPayload?: HotelDestinationPayload | null
    destinationKind?: HotelDestinationKind | null
    checkIn?: Date | null
    checkOut?: Date | null
    roomConfigs?: HotelRoomConfig[]
}

export const useHotelSearchManager = (initialValues?: HotelSearchInitialValues) => {
    const router = useRouter()
    // Seeded lazily: the modifier mounts with the active search already applied, the
    // homepage form mounts empty. Later edits are owned by this hook either way.
    const [destination, setDestination] = useState(() => initialValues?.destination ?? '')
    const [selectedDestinationPayload, setSelectedDestinationPayload] = useState<HotelDestinationPayload | null>(
        () => initialValues?.destinationPayload ?? null
    )
    const [selectedDestinationKind, setSelectedDestinationKind] = useState<HotelDestinationKind | null>(
        () => initialValues?.destinationKind ?? null
    )
    // What is actually queried: the input text once the user stops typing.
    const [debouncedPrefix, setDebouncedPrefix] = useState('')

    const selectingDestinationRef = useRef(false)

    const handleDestinationInputChange = useCallback((nextText: string) => {
        selectingDestinationRef.current = false
        setDestination(nextText)
        setSelectedDestinationPayload(null)
        setSelectedDestinationKind(null)
    }, [])

    const handleDestinationSelect = useCallback((suggestion: HotelDestinationSuggestion) => {
        selectingDestinationRef.current = true
        setDestination(suggestion.label)
        setSelectedDestinationPayload(suggestion.payload)
        setSelectedDestinationKind(suggestion.kind)
        setDebouncedPrefix('')
    }, [])

    useEffect(() => {
        const query = destination.trim()

        // Picking a suggestion rewrites the input to that label. That is not a new search.
        if (selectingDestinationRef.current) {
            selectingDestinationRef.current = false
            return
        }

        if (query.length < HOTEL_AUTO_SUGGEST_MIN_CHARS) {
            setDebouncedPrefix('')
            return
        }

        const timer = setTimeout(() => setDebouncedPrefix(query), HOTEL_AUTO_SUGGEST_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [destination])

    // RTK Query owns request ordering and caching, so no manual staleness guard is needed:
    // a superseded prefix can never overwrite the current one's results.
    const { data: autoSuggestItems, isFetching: destinationLoading } = useGetHotelAutoSuggestQuery(
        { prefix: debouncedPrefix },
        { skip: debouncedPrefix.length < HOTEL_AUTO_SUGGEST_MIN_CHARS }
    )

    const destinationSuggestions = useMemo<HotelDestinationSuggestion[]>(
        () =>
            (autoSuggestItems || []).map((item, index) => ({
                key: `${item.kind}_${index}_${item.hotelId || item.cityId}`,
                kind: item.kind,
                primary: item.primary,
                secondary: item.secondary,
                label: item.label,
                propertyCount: item.propertyCount,
                payload: {
                    // A city has no property id of its own, so it identifies itself by city.
                    uniqueId: item.hotelId || item.cityId,
                    cityId: item.cityId,
                    cityName: item.cityName,
                    provinceName: item.stateName,
                    provinceId: item.stateId,
                    countryName: item.countryName,
                    countryCode: item.countryCode,
                    countryId: item.countryId,
                    ...(item.hotelName ? { hotelName: item.hotelName } : {})
                }
            })),
        [autoSuggestItems]
    )

    const [checkIn, setCheckIn] = useState<Date | null>(() => initialValues?.checkIn ?? null)
    const [checkOut, setCheckOut] = useState<Date | null>(() => initialValues?.checkOut ?? null)
    const [roomConfigs, setRoomConfigs] = useState<HotelRoomConfig[]>(
        () => initialValues?.roomConfigs ?? [{ ...DEFAULT_ROOM_CONFIG }]
    )

    const totalRooms = roomConfigs.length
    const totalAdults = useMemo(() => roomConfigs.reduce((sum, r) => sum + r.adults, 0), [roomConfigs])
    const totalChildren = useMemo(() => roomConfigs.reduce((sum, r) => sum + r.children, 0), [roomConfigs])
    const totalGuests = totalAdults + totalChildren

    const nights = useMemo(() => {
        if (!checkIn || !checkOut) return null
        const diff = startOfDay(checkOut).diff(startOfDay(checkIn), 'day')
        return diff > 0 ? diff : null
    }, [checkIn, checkOut])

    const handleCheckInChange = useCallback((date: Date | null) => {
        setCheckIn(date)
        if (!date) return

        setCheckOut(prev => {
            if (prev && isSameOrBefore(prev, date)) {
                return null
            }
            return prev
        })
    }, [])

    const handleCheckOutChange = useCallback(
        (date: Date | null) => {
            if (!date) {
                setCheckOut(null)
                return
            }
            if (checkIn && isSameOrBefore(date, checkIn)) {
                return
            }
            setCheckOut(date)
        },
        [checkIn]
    )

    const handleRoomCountChange = useCallback((count: number) => {
        const next = Math.min(MAX_ROOMS, Math.max(MIN_ROOMS, count))
        setRoomConfigs(prev => {
            if (next === prev.length) return prev
            if (next > prev.length) {
                const added = Array.from({ length: next - prev.length }, () => ({ ...DEFAULT_ROOM_CONFIG }))
                return [...prev, ...added]
            }
            return prev.slice(0, next)
        })
    }, [])

    const handleRoomAdultsChange = useCallback((roomIndex: number, adults: number) => {
        const val = Math.min(MAX_ADULTS_PER_ROOM, Math.max(MIN_ADULTS_PER_ROOM, adults))
        setRoomConfigs(prev => prev.map((room, i) => (i === roomIndex ? { ...room, adults: val } : room)))
    }, [])

    const handleRoomChildrenChange = useCallback((roomIndex: number, children: number) => {
        const val = Math.min(MAX_CHILDREN_PER_ROOM, Math.max(MIN_CHILDREN_PER_ROOM, children))
        setRoomConfigs(prev =>
            prev.map((room, i) =>
                i === roomIndex ? { ...room, children: val, childAges: resizeChildAges(room.childAges, val) } : room
            )
        )
    }, [])

    const getSearchParams = useCallback(
        () => ({
            destination,
            destinationPayload: selectedDestinationPayload,
            checkIn,
            checkOut,
            nights,
            roomConfigs,
            totalRooms,
            totalAdults,
            totalChildren,
            totalGuests
        }),
        [
            destination,
            selectedDestinationPayload,
            checkIn,
            checkOut,
            nights,
            roomConfigs,
            totalRooms,
            totalAdults,
            totalChildren,
            totalGuests
        ]
    )

    const isSearchReady = Boolean(
        selectedDestinationPayload && selectedDestinationKind && checkIn && checkOut && nights
    )

    const minCheckOutDate = checkIn ? addDays(checkIn, 1) : new Date()

    // Destination path. A city goes to the SRP; a specific property goes straight to its
    // details page, because the upstream's Hotel-type search returns a single property's
    // review payload rather than a result list — routing it to the SRP yields an empty list.
    const getHotelDestinationPath = useCallback(
        (locale: string) => {
            if (!selectedDestinationPayload || !selectedDestinationKind || !checkIn || !checkOut || !nights) {
                return null
            }

            const params = new URLSearchParams()
            params.set('checkInDate', getFormattedDate(checkIn, 'YYYY-MM-DD'))
            params.set('checkOutDate', getFormattedDate(checkOut, 'YYYY-MM-DD'))
            params.set('nights', String(nights))
            params.set('noOfRooms', String(roomConfigs.length))
            params.set('noOfAdults', roomConfigs.map(room => room.adults).join(','))
            params.set('noOfChildren', roomConfigs.map(room => room.children).join(','))
            params.set('childrenAges', encodeChildrenAges(roomConfigs))
            params.set('cityId', selectedDestinationPayload.cityId || '')
            params.set(
                'cityName',
                selectedDestinationPayload.cityName || destination.split(',')[0]?.trim() || destination
            )
            params.set('countryName', selectedDestinationPayload.countryName || '')

            if (selectedDestinationKind === 'hotel') {
                if (selectedDestinationPayload.hotelName) params.set('hotelName', selectedDestinationPayload.hotelName)
                // No echoToken: the details page has no prior search to select against, so it
                // runs the Hotel-type search itself (Path B in useHotelReviewData).
                return `/${locale}/hotels/details/${selectedDestinationPayload.uniqueId}?${params.toString()}`
            }

            return `/${locale}/hotels/results?${params.toString()}`
        },
        [selectedDestinationPayload, selectedDestinationKind, destination, checkIn, checkOut, nights, roomConfigs]
    )

    const performHotelSearch = useCallback(
        (locale: string) => {
            const path = getHotelDestinationPath(locale)
            if (!path) return false
            // Path is non-null only when destination/kind/dates are all set, so these are safe.
            saveHotelLastSearch({
                destination,
                destinationPayload: selectedDestinationPayload!,
                destinationKind: selectedDestinationKind!,
                checkIn: checkIn!,
                checkOut: checkOut!,
                roomConfigs
            })
            router.push(path)
            return true
        },
        [getHotelDestinationPath, router, destination, selectedDestinationPayload, selectedDestinationKind, checkIn, checkOut, roomConfigs]
    )

    return {
        destination,
        handleDestinationInputChange,
        handleDestinationSelect,
        destinationSuggestions,
        destinationLoading,
        selectedDestinationPayload,
        checkIn,
        checkOut,
        nights,
        roomConfigs,
        totalRooms,
        totalAdults,
        totalChildren,
        totalGuests,
        handleCheckInChange,
        handleCheckOutChange,
        handleRoomCountChange,
        handleRoomAdultsChange,
        handleRoomChildrenChange,
        getSearchParams,
        getHotelDestinationPath,
        performHotelSearch,
        isSearchReady,
        minCheckOutDate,
        minRooms: MIN_ROOMS,
        maxRooms: MAX_ROOMS,
        minAdultsPerRoom: MIN_ADULTS_PER_ROOM,
        maxAdultsPerRoom: MAX_ADULTS_PER_ROOM,
        minChildrenPerRoom: MIN_CHILDREN_PER_ROOM,
        maxChildrenPerRoom: MAX_CHILDREN_PER_ROOM
    }
}
